import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom"; // Import useNavigate
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import colors from "../../styles/colors";
import LogoStroke from "../../styles/LogoStroke.svg"; // Import the SVG logo

interface HeaderProps {
  title: string;
  navLinks: { label: string; href: string }[];
}

const Header: React.FC<HeaderProps> = ({ title, navLinks }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isClicked, setIsClicked] = useState(false);

  const location = useLocation(); // Get the current route
  const navigate = useNavigate(); // Hook to navigate programmatically

  // Determine header styles based on the current route
  const isSubPage = location.pathname !== "/"; // Check if the user is on a subpage
  const headerStyles = {
    backgroundColor: isSubPage ? colors.lightgray : colors.warmBeige,
    color: isSubPage ? colors.warmBeige : colors.lightgray,
    filter: isSubPage? "brightness(0.01)" : "brightness(1)", // Adjust brightness based on the route
    
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsClicked(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsClicked(false);
  };

  const handleLogoClick = () => {
    navigate("/"); // Navigate to the main page
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: headerStyles.backgroundColor,
        height: "10vh",
        color: headerStyles.color,
        transition: "background-color 0.3s ease, color 0.3s ease", // Smooth transition
        boxShadow: "none",
      }}
    >
      <Toolbar
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        {/* Logo Button */}
        <Button
          onClick={handleLogoClick}
          sx={{
            backgroundColor: "transparent",
            padding: 0,
            minWidth: 0,
            marginRight: "2rem", 
            marginLeft: "2rem",
            filter: headerStyles.filter,
            "&:hover": {
              backgroundColor: "transparent",
              filter: "brightness(0.5)", 
              transition: "filter 0.3s ease",
            },
          }}
        >
          <img
            src={LogoStroke}
            alt="Logo"
            style={{
              height: "3.5rem", // Adjust the size of the logo
              width: "auto",
            }}
          />
        </Button>

        {/* Title */}
        <Button
          sx={{
            fontFamily: "'Gantari', sans-serif",
            color: "white",
            borderRadius: 0,
            cursor: "pointer",
            position: "relative",
            fontSize: "1.5rem",
            fontWeight: 400,
            textTransform: "none",
            backgroundColor: "transparent",
            padding: "0rem 1rem",
            filter: headerStyles.filter,
            "&:hover": {
              backgroundColor: "transparent",
              filter: "brightness(0.5)", // Adjust brightness on hover
              transition: "filter 0.3s ease",
              textDecoration: "none",
            },
          }}
          onClick={handleClick}
        >
          {title}
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          sx={{
            "& .MuiPaper-root": {
              backgroundColor: headerStyles.backgroundColor,
            },
          }}
        >
          {navLinks.map((link, index) => (
            <MenuItem
              key={index}
              onClick={() => {
                handleClose();
                navigate(link.href);
              }}
              sx={{
                filter: headerStyles.filter,
                "&:hover": {
                  filter: "brightness(0.5)",
                },
              }}
            >
              <Button disableRipple color="inherit">
                {link.label}
              </Button>
            </MenuItem>
          ))}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;